/** Native haptic bridge — wire to Capacitor / React Native on device builds. */

export type CalibrationHapticBridge = {
  /** Light continuous pulse during eccentric descent (call ~every 300 ms). */
  eccentricDescentPulse: () => void
  /** Aggressive double-pulse at isometric bottom. */
  isometricDoublePulse: () => void
  /** Deep thud when rep completes. */
  repCompletionThud: () => void
  stopContinuous: () => void
}

function vibrate(pattern: number | number[]): void {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(pattern)
  }
}

/** Web fallback using the Vibration API. */
export function createWebHapticBridge(): CalibrationHapticBridge {
  let continuousTimer: ReturnType<typeof setInterval> | null = null

  return {
    eccentricDescentPulse: () => vibrate(25),
    isometricDoublePulse: () => vibrate([80, 40, 120]),
    repCompletionThud: () => vibrate(180),
    stopContinuous: () => {
      if (continuousTimer) {
        clearInterval(continuousTimer)
        continuousTimer = null
      }
      vibrate(0)
    },
  }
}

/** Starts a 3 s eccentric descent haptic stream (300 ms cadence). */
export function startEccentricHapticStream(bridge: CalibrationHapticBridge): void {
  bridge.stopContinuous()
  bridge.eccentricDescentPulse()
  const timer = setInterval(() => bridge.eccentricDescentPulse(), 300)
  ;(bridge as CalibrationHapticBridge & { _eccentricTimer?: ReturnType<typeof setInterval> })._eccentricTimer =
    timer
}

export function stopEccentricHapticStream(bridge: CalibrationHapticBridge): void {
  const extended = bridge as CalibrationHapticBridge & { _eccentricTimer?: ReturnType<typeof setInterval> }
  if (extended._eccentricTimer) {
    clearInterval(extended._eccentricTimer)
    extended._eccentricTimer = undefined
  }
  bridge.stopContinuous()
}
